import { useEffect, useState } from "react";
import { api, eur, frDate, openPdf } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, FileDown } from "lucide-react";

const empty = {
  employee_id: "", employee_name: "", position: "", social_security: "",
  period: "", issue_date: new Date().toISOString().slice(0, 10),
  gross_salary: 0, social_deductions: 0, other_deductions: 0, notes: "",
};

export default function Bulletins() {
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/payslips").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
  }, []);

  const pickEmployee = (id) => {
    const e = employees.find((x) => x.id === id);
    if (!e) return;
    const deductions = Math.round(e.gross_monthly_salary * 0.22 * 100) / 100;
    setForm((s) => ({
      ...s,
      employee_id: e.id,
      employee_name: `${e.first_name} ${e.last_name}`,
      position: e.position,
      social_security: e.social_security,
      gross_salary: e.gross_monthly_salary,
      social_deductions: deductions,
    }));
  };

  const save = async () => {
    if (!form.employee_name || !form.period) { toast.error("Salarié et période requis"); return; }
    try {
      const payload = {
        ...form,
        gross_salary: Number(form.gross_salary) || 0,
        social_deductions: Number(form.social_deductions) || 0,
        other_deductions: Number(form.other_deductions) || 0,
      };
      if (edit) await api.put(`/payslips/${edit.id}`, payload);
      else await api.post("/payslips", payload);
      toast.success(edit ? "Bulletin modifié." : "Bulletin créé.");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const remove = async (p) => {
    if (!window.confirm("Supprimer ce bulletin ?")) return;
    await api.delete(`/payslips/${p.id}`); load();
  };

  const net = (p) => Number(p.gross_salary) - Number(p.social_deductions) - Number(p.other_deductions);

  return (
    <div data-testid="bulletins-page">
      <PageHeader
        eyebrow="Paie"
        title="Bulletins de paie"
        description="Éditez les bulletins de paie des salarié(e)s et générez le PDF officiel."
        actions={
          <Button data-testid="new-payslip-btn" onClick={() => { setEdit(null); setForm(empty); setOpen(true); }} className="bg-brand-orange hover:bg-[#D04A06] text-white">
            <Plus size={16} className="mr-1" /> Nouveau bulletin
          </Button>
        }
      />

      <div className="bg-white border border-brand-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 small-caps">
            <tr>
              <th className="text-left py-3 px-4">Salarié(e)</th>
              <th className="text-left py-3 px-4">Période</th>
              <th className="text-left py-3 px-4">Date</th>
              <th className="text-right py-3 px-4">Brut</th>
              <th className="text-right py-3 px-4">Net à payer</th>
              <th className="text-right py-3 px-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-gray-400">Aucun bulletin pour l'instant.</td></tr>}
            {rows.map((p) => (
              <tr key={p.id} className="border-t border-brand-border hover:bg-gray-50">
                <td className="py-3 px-4 font-medium text-gray-900">{p.employee_name}<div className="text-xs text-gray-400">{p.position}</div></td>
                <td className="py-3 px-4 text-gray-700">{p.period}</td>
                <td className="py-3 px-4 text-gray-700">{frDate(p.issue_date)}</td>
                <td className="py-3 px-4 text-right text-gray-700">{eur(p.gross_salary)}</td>
                <td className="py-3 px-4 text-right font-semibold text-brand-green">{eur(net(p))}</td>
                <td className="py-3 px-4 text-right space-x-1">
                  <Button size="sm" variant="outline" onClick={() => openPdf(`/payslips/${p.id}/pdf`)}><FileDown size={14} className="mr-1" />PDF</Button>
                  <Button size="sm" variant="ghost" onClick={() => { setEdit(p); setForm({...empty, ...p}); setOpen(true); }}><Pencil size={14} /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(p)}><Trash2 size={14} className="text-red-500" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-brand-navy">{edit ? "Modifier le bulletin" : "Nouveau bulletin"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            <div className="md:col-span-2">
              <Label>Salarié(e)</Label>
              <Select value={form.employee_id || ""} onValueChange={pickEmployee}>
                <SelectTrigger><SelectValue placeholder="Choisir dans la liste des salariés..." /></SelectTrigger>
                <SelectContent>
                  {employees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name} — {e.position}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-400 mt-1">Ou saisir manuellement ci-dessous.</p>
            </div>
            <div><Label>Nom complet *</Label><Input value={form.employee_name} onChange={(e) => setForm({...form, employee_name: e.target.value})} /></div>
            <div><Label>Poste</Label><Input value={form.position} onChange={(e) => setForm({...form, position: e.target.value})} /></div>
            <div><Label>N° SS</Label><Input value={form.social_security} onChange={(e) => setForm({...form, social_security: e.target.value})} /></div>
            <div><Label>Période *</Label><Input value={form.period} onChange={(e) => setForm({...form, period: e.target.value})} placeholder="Janvier 2026" /></div>
            <div><Label>Date d'émission</Label><Input type="date" value={form.issue_date} onChange={(e) => setForm({...form, issue_date: e.target.value})} /></div>
            <div><Label>Salaire brut (€)</Label><Input type="number" step="0.01" value={form.gross_salary} onChange={(e) => setForm({...form, gross_salary: e.target.value})} /></div>
            <div><Label>Cotisations sociales (€)</Label><Input type="number" step="0.01" value={form.social_deductions} onChange={(e) => setForm({...form, social_deductions: e.target.value})} /></div>
            <div><Label>Autres retenues (€)</Label><Input type="number" step="0.01" value={form.other_deductions} onChange={(e) => setForm({...form, other_deductions: e.target.value})} /></div>
            <div className="flex items-end">
              <div className="w-full bg-brand-navy/5 rounded-md p-3">
                <div className="small-caps">Net à payer</div>
                <div className="font-serif text-2xl text-brand-green">{eur(Number(form.gross_salary) - Number(form.social_deductions) - Number(form.other_deductions))}</div>
              </div>
            </div>
            <div className="md:col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} className="bg-brand-orange hover:bg-[#D04A06] text-white">{edit ? "Enregistrer" : "Créer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
